import { useEffect, useState } from "react";
import {
  Avatar,
  InlineLink,
  Input,
  PencilIcon,
  Table,
  Toggle,
  TrashIcon,
  SearchIcon,
  NotifierNotification,
  ConfirmationModal,
} from "@mwi/ui";
import useTitle from "hooks/useTitle";
import CreateAdminModal from "components/modals/admin/create";
import {
  useDeleteUserMutation,
  useGetUsersQuery,
  useResendVerifyMutation,
  useUpdateUserMutation,
} from "api";
import { DateTime } from "luxon";
import { useDebounce } from "use-debounce";
import { useNotifier } from "react-headless-notifier";
import UpdateAdminModal from "components/modals/admin/update";
import classNames from "classnames";
import useLazyTimeout from "hooks/useLazyTimeout";
import User from "api/types/models/user";

type ItemProps = {
  user: User;
  onChange: (id: number, v: boolean) => void;
  onClick: (action?: "edit" | "delete" | "resend") => void;
};

const Item = ({ user, onClick, onChange }: ItemProps): JSX.Element => {
  const [toggle, triggerResend] = useLazyTimeout({ delay: 10000 });

  const handleResend = () => {
    if (!toggle) {
      onClick("resend");
      triggerResend();
    }
  };

  return (
    <Table.Row key={user.id}>
      <Table.Item className="flex items-center gap-2">
        <Avatar
          className="w-10 h-10"
          name={
            user.first_name ? `${user.first_name} ${user.last_name}` : undefined
          }
        />
        {user.first_name ? `${user.first_name} ${user.last_name}` : "New User"}
      </Table.Item>
      <Table.Item>
        <div className="space-x-2">
          <span>{user.email}</span>
          {!user.verified_at && (
            <span
              className={classNames("font-semibold text-primary select-none", {
                "cursor-pointer": !toggle,
              })}
              onClick={() => handleResend()}
            >
              {`(${toggle ? "Link resent" : "Resend verify link"})`}
            </span>
          )}
        </div>
      </Table.Item>
      <Table.Item>
        {user.last_active_at ? (
          DateTime.fromISO(user.last_active_at).toFormat("dd.MM.yyyy")
        ) : (
          <div className="space-x-2">
            <span className="font-semibold text-light">Pending</span>
          </div>
        )}
      </Table.Item>
      <Table.Item>
        <Toggle value={user.is_active} onChange={(v) => onChange(user.id, v)} />
      </Table.Item>
      <Table.Item className="flex justify-between">
        <div
          className="inline-block cursor-pointer"
          onClick={() => onClick("edit")}
        >
          <PencilIcon className="w-6 h-6 text-primary" />
        </div>
        <div
          className="inline-block cursor-pointer"
          onClick={() => onClick("delete")}
        >
          <TrashIcon className="w-6 h-6 text-error" />
        </div>
      </Table.Item>
    </Table.Row>
  );
};

const UserManagementPage = (): JSX.Element => {
  useTitle("Manage Accounts");

  const { notify } = useNotifier();

  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    useState(false);
  const [search, setSearch] = useState<string>();
  const [updatingUserId, setUpdatingUserId] = useState<number | undefined>();
  const [deletingUserId, setDeletingUserId] = useState<number | undefined>();

  const [debouncedSearch] = useDebounce(search, 400);

  const { data: users, isFetching } = useGetUsersQuery({
    search: debouncedSearch,
  });

  const [resendVerify, { isError: resendVerifyError }] =
    useResendVerifyMutation();

  const [updateUser, { isSuccess: didUpdateUser }] = useUpdateUserMutation();
  const [deleteUser, { isLoading: isDeleting, isSuccess: didDeleteUser }] =
    useDeleteUserMutation();

  const handleToggleUser = async (id, value) => {
    await updateUser({ id, body: { is_active: value } }).unwrap();
    setDeletingUserId(undefined);
  };

  const handleUser = async (
    user: User,
    action?: "edit" | "delete" | "resend",
  ) => {
    if (action === "edit") {
      setUpdatingUserId(user.id);
    } else if (action === "delete") {
      setDeletingUserId(user.id);
    } else if (action === "resend") {
      await resendVerify({ id: user.id });
    }
  };

  useEffect(() => {
    if (didUpdateUser) {
      notify(
        <NotifierNotification
          title="User Updated"
          message="User was successfully updated"
        />,
      );
    }
  }, [didUpdateUser]);

  useEffect(() => {
    if (didDeleteUser) {
      setDeletingUserId(undefined);
      notify(
        <NotifierNotification
          title="User Deleted"
          message="User was successfully deleted"
        />,
      );
    }
  }, [didDeleteUser]);

  useEffect(() => {
    if (resendVerifyError) {
      notify(
        <NotifierNotification
          type="error"
          title="Resend Verification Error"
          message="We're unable to resend the verification link, please try again later."
        />,
      );
    }
  }, [resendVerifyError]);

  return (
    <div className="grid tablet:grid-cols-8 desktop:grid-cols-10 !pb-16 gap-x-6">
      <div className="flex flex-col gap-y-[2px] col-span-full mb-14 text-lg">
        <p className="font-semibold">
          Manage the people who have access to the platform as an MWI admin
        </p>
        <p>
          Here you have the option to add, delete or deactivate MWI admin
          accounts
        </p>
      </div>

      <div className="mb-8 col-span-full tablet:col-span-2 desktop:col-span-3">
        <Input
          placeHolder="Search users"
          size="small"
          prefixIcon={<SearchIcon />}
          onChange={(e) =>
            setSearch(e.target.value ? e.target.value : undefined)
          }
        />
      </div>

      <div className="col-span-full">
        <Table loading={isFetching}>
          <Table.Head>
            <Table.Row>
              <Table.Item as="th">Name</Table.Item>
              <Table.Item as="th">Email</Table.Item>
              <Table.Item as="th">Last Active</Table.Item>
              <Table.Item as="th">Active</Table.Item>
              <Table.Item as="th"></Table.Item>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {users && users.length ? (
              users.map((user) => (
                <Item
                  key={user.id}
                  user={user}
                  onChange={handleToggleUser}
                  onClick={(action) => handleUser(user, action)}
                />
              ))
            ) : (
              <Table.Row>
                <Table.Item colSpan={5} className="text-center">
                  No users found
                </Table.Item>
              </Table.Row>
            )}
          </Table.Body>
        </Table>

        <div className="mt-8">
          <InlineLink onClick={() => setIsCreateAccountModalOpen(true)}>
            + Add new user
          </InlineLink>
        </div>
      </div>

      <CreateAdminModal
        isOpen={isCreateAccountModalOpen}
        onClose={() => setIsCreateAccountModalOpen(false)}
      />

      <UpdateAdminModal
        isOpen={updatingUserId !== undefined}
        onClose={() => setUpdatingUserId(undefined)}
        user={users?.find((u) => u.id === updatingUserId)!}
      />

      <ConfirmationModal
        isOpen={deletingUserId !== undefined}
        onClose={() => setDeletingUserId(undefined)}
        title="Delete User"
        description="Are you sure you want to delete this user?"
        confirmText="Delete"
        confirmLoading={isDeleting}
        onConfirm={() => deleteUser(deletingUserId!)}
        danger
      />
    </div>
  );
};

export default UserManagementPage;
